import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef } from "react";

export interface UsePortalKeepAliveOptions {
  /**
   * The session "key" – this is userKey for User kind, customerId for Customer kind.
   */
  key?: string | number | null;
  /**
   * The portal session key (guid).
   */
  sessionKey?: string | null;
  /**
   * "user" | "customer"
   */
  kind?: string | null;
  /**
   * Optional handler invoked with each RequestKeepAlive payload from the server.
   */
  onKeepAlive?: (payload: unknown) => void;
  /**
   * Optional handler invoked when the server forces a logout.
   */
  onLogout?: (reason: string) => void;
}

/**
 * React port of the old portal-api.js client. Establishes a SignalR connection
 * to /ws_portal, activates the session with (key, sessionKey, kind) and replies
 * to each RequestKeepAlive ping with KeepAliveSession. RequestLogout triggers
 * onLogout or redirects to /login?reason=expired by default.
 */
export function usePortalKeepAlive(options: UsePortalKeepAliveOptions) {
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    const { key, sessionKey, kind, onKeepAlive, onLogout } = options;
    if (!key || !sessionKey || !kind) return;

    // WebSockets without the negotiate round-trip: one request opens the
    // connection on one pod, so no sticky sessions are needed. When a proxy
    // blocks WebSockets the fallback below negotiates and long-polls instead.
    const build = (websocketsOnly: boolean) =>
      new HubConnectionBuilder()
        .withUrl("/ws_portal", websocketsOnly ? { skipNegotiation: true, transport: HttpTransportType.WebSockets } : {})
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();
    let connection = build(true);

    connectionRef.current = connection;

    const wire = (c: HubConnection) => {
      c.on("RequestKeepAlive", (payload: unknown) => {
        onKeepAlive?.(payload);
        void c.invoke("KeepAliveSession", String(key), sessionKey, kind).catch((err) => {
          console.warn("KeepAliveSession failed", err);
        });
      });
      c.on("RequestLogout", (reason: string) => {
        if (onLogout) {
          onLogout(reason);
        } else {
          window.location.href = "/login?reason=expired";
        }
      });
    };
    wire(connection);

    // Track the start() promise so cleanup can await it before stopping.
    // Without this, React 18 StrictMode (double-mount in dev) aborts the
    // in-flight negotiation and logs "The connection was stopped during
    // negotiation" on every page load.
    let cancelled = false;
    const startPromise = connection
      .start()
      .catch(async (err) => {
        // WebSockets refused (proxy without upgrade support): retry with negotiation.
        if (cancelled) throw err;
        const first = connection;
        connection = build(false);
        connectionRef.current = connection;
        wire(connection);
        void first.stop().catch(() => undefined);
        await connection.start();
      })
      .then(() => {
        if (cancelled) return;
        return connection.invoke("ActivateSession", String(key), sessionKey, kind);
      })
      .catch((err) => {
        // Negotiation aborts are expected when StrictMode (or a quick navigation)
        // unmounts before the handshake completes — swallow them silently.
        if (cancelled) return;
        const name = err?.name ?? "";
        const msg = String(err?.message ?? err ?? "");
        if (name === "AbortError" || msg.includes("stopped during negotiation")) {
          return;
        }
        console.warn("SignalR connect failed", err);
      });

    return () => {
      cancelled = true;
      connection.off("RequestKeepAlive");
      connection.off("RequestLogout");
      void startPromise.finally(() => {
        if (connection.state !== HubConnectionState.Disconnected) {
          void connection.stop().catch(() => undefined);
        }
      });
      connectionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.key, options.sessionKey, options.kind]);
}
